import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';

const Preise = () => {
  return (
    <div className="bg-white py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Breadcrumb items={[{ label: 'Preise & Ablauf' }]} />
        
        <h1 className="text-4xl font-bold text-[#0F172A] mb-6">Preise & Ablauf – transparent zum Festpreis</h1>
        <p className="text-lg text-gray-700 mb-12 max-w-3xl">
          Was kostet eine Haushaltsauflösung in Dortmund? Die Antwort ist: Es kommt darauf an. 
          Wir erklären Ihnen transparent, welche Faktoren den Preis beeinflussen und wie Sie 
          schnell zu einem verbindlichen Festpreis-Angebot kommen.
        </p>

        {/* Price Factors */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-6">Was beeinflusst den Preis?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Objektgröße</h3>
                <p className="text-gray-700">Je mehr Zimmer und Räume (Keller, Dachboden), desto höher der Aufwand. Ein Einfamilienhaus kostet mehr als eine 1-Zimmer-Wohnung.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Zugänglichkeit</h3>
                <p className="text-gray-700">Enge Treppenhäuser, fehlende Aufzüge, Altbauten oder Parkverbotszonen erhöhen den Aufwand und werden einkalkuliert.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Sortieraufwand</h3>
                <p className="text-gray-700">Messi-Wohnungen oder stark vermüllte Objekte erfordern mehr Zeit und Schutzmaßnahmen, was sich im Preis widerspiegelt.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Sondermüll</h3>
                <p className="text-gray-700">Farben, Lacke, Chemikalien oder Asbest müssen fachgerecht entsorgt werden. Diese Kosten werden separat ausgewiesen.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Zusatzleistungen</h3>
                <p className="text-gray-700">Demontagen, Renovierungsarbeiten, Grundreinigung oder Dokumentensicherung können optional hinzugebucht werden.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Wertanrechnung</h3>
                <p className="text-gray-700">Verwertbare Gegenstände werden transparent angerechnet und reduzieren Ihren Endpreis erheblich.</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Price Ranges */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-6">Typische Preisspannen</h2>
          <p className="text-gray-700 mb-8">Diese Spannen dienen der Orientierung. Ihren exakten Festpreis erhalten Sie nach Besichtigung oder Foto-Zusendung.</p>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-2 border-gray-200">
              <CardContent className="pt-6 text-center">
                <h3 className="text-2xl font-bold text-[#0F172A] mb-2">1-Zimmer-Wohnung</h3>
                <p className="text-4xl font-bold text-[#16A34A] mb-3">600 – 1.200 €</p>
                <ul className="text-left text-sm text-gray-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#16A34A] flex-shrink-0 mt-0.5" />
                    <span>Inkl. besenreiner Übergabe</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#16A34A] flex-shrink-0 mt-0.5" />
                    <span>Entsorgung inklusive</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#16A34A] flex-shrink-0 mt-0.5" />
                    <span>Wertanrechnung möglich</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-2 border-[#16A34A]">
              <CardContent className="pt-6 text-center">
                <h3 className="text-2xl font-bold text-[#0F172A] mb-2">2-3 Zimmer-Wohnung</h3>
                <p className="text-4xl font-bold text-[#16A34A] mb-3">900 – 1.800 €</p>
                <ul className="text-left text-sm text-gray-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#16A34A] flex-shrink-0 mt-0.5" />
                    <span>Inkl. Küche & Bad</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#16A34A] flex-shrink-0 mt-0.5" />
                    <span>Keller optional</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#16A34A] flex-shrink-0 mt-0.5" />
                    <span>Festpreis-Garantie</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-200">
              <CardContent className="pt-6 text-center">
                <h3 className="text-2xl font-bold text-[#0F172A] mb-2">Einfamilienhaus</h3>
                <p className="text-4xl font-bold text-[#16A34A] mb-3">1.800 – 4.500 €</p>
                <ul className="text-left text-sm text-gray-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#16A34A] flex-shrink-0 mt-0.5" />
                    <span>Inkl. Keller & Dachboden</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#16A34A] flex-shrink-0 mt-0.5" />
                    <span>Gartengeräte optional</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#16A34A] flex-shrink-0 mt-0.5" />
                    <span>Demontagen inklusive</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Package Comparison */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-6">Unsere Pakete</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Basis</h3>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0" />
                    <span className="text-gray-700">Komplette Räumung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0" />
                    <span className="text-gray-700">Besenrein</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0" />
                    <span className="text-gray-700">Fachgerechte Entsorgung</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-2 border-[#16A34A]">
              <CardContent className="pt-6">
                <div className="bg-[#16A34A] text-white px-3 py-1 rounded-full text-sm inline-block mb-4">Beliebt</div>
                <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Festpreis</h3>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0" />
                    <span className="text-gray-700">Alles aus Basis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0" />
                    <span className="text-gray-700">Verbindlicher Festpreis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0" />
                    <span className="text-gray-700">Wertanrechnung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0" />
                    <span className="text-gray-700">Entsorgungsnachweis</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Komfort</h3>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0" />
                    <span className="text-gray-700">Alles aus Festpreis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0" />
                    <span className="text-gray-700">Demontagen inklusive</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0" />
                    <span className="text-gray-700">Dokumentensicherung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0" />
                    <span className="text-gray-700">Grundreinigung</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Value Credit Example */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-6">Rechenbeispiel: Wertanrechnung</h2>
          <Card className="bg-gray-50 border-2 border-[#16A34A]">
            <CardContent className="pt-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-[#0F172A] mb-4">Beispiel: 3-Zimmer-Wohnung</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Basispreis Räumung:</span>
                      <span className="font-semibold">1.800 €</span>
                    </div>
                    <div className="border-t border-gray-300 pt-3">
                      <p className="text-sm text-gray-600 mb-2">Anrechenbare Werte:</p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-700">Küchenzeile (funktionsfähig):</span>
                          <span>-400 €</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Wohnzimmerschrank (Eiche):</span>
                          <span>-300 €</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Elektrogeräte (TV, Waschmaschine):</span>
                          <span>-250 €</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Bücher & Deko:</span>
                          <span>-100 €</span>
                        </div>
                      </div>
                    </div>
                    <div className="border-t-2 border-[#16A34A] pt-3 text-xl font-bold flex justify-between">
                      <span>Ihr Endpreis:</span>
                      <span className="text-[#16A34A]">750 €</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <p className="text-gray-700">
                    Durch die transparente Wertanrechnung sparen Sie über die Hälfte! 
                    Wir schätzen alle verwertbaren Gegenstände fair und ziehen diese vom Gesamtpreis ab. 
                    Sie erhalten eine detaillierte Aufstellung.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <section className="bg-[#0F172A] text-white rounded-lg p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">Jetzt Ihren Festpreis anfordern</h2>
          <p className="text-lg mb-8 text-gray-300 max-w-2xl mx-auto">
            Kontaktieren Sie uns per Telefon, WhatsApp oder Formular. 
            Nach kurzer Abstimmung erhalten Sie Ihr verbindliches Angebot.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#16A34A] hover:bg-[#16A34A]/90 text-white">
              <Link to="/kontakt">Zum Formular</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-white text-[#0F172A] hover:bg-gray-100">
              <a href="tel:+4923139085116">0231 39085116</a>
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Preise;